import { useMemo, useState, type ReactNode } from 'react'

import { formatHM, formatHMS, formatMoney } from '../lib/format'
import { local, queueOp } from '../lib/sync'
import {
  PAYMENT_STATUS_LABEL,
  PRIORITY_LABEL,
  WORK_STATUS_LABEL,
  type Task,
  type WorkStatus,
} from '../types'
import { IconBack, IconPause, IconPlay, IconStop } from './icons'
import { humanDate } from './Journal'
import { useLocal, useTicker } from './useLocal'

/** Статусы, которые техник ставит с телефона. Статус оплаты не трогаем:
 *  это решение расчётчика, и ошибка здесь дороже одного сэкономленного нажатия. */
const PICKABLE: WorkStatus[] = ['new', 'in_progress', 'paused', 'waiting_client', 'done']

export default function TaskCard({ taskId, onBack, onToast }: {
  taskId: number
  onBack: () => void
  onToast: (text: string) => void
}) {
  const { rows: tasks } = useLocal<Task>(local.tasks)
  const task = useMemo(() => tasks.find((t) => t.id === taskId) ?? null, [tasks, taskId])
  const [statusOpen, setStatusOpen] = useState(false)

  useTicker(!!task?.running_since)

  if (!task) {
    return (
      <>
        <Bar title="Задача" onBack={onBack} />
        <div className="m-empty">Задача не найдена в загруженных данных</div>
      </>
    )
  }

  const live = !!task.running_since
  const seconds = live
    ? task.tracked_seconds +
      Math.max(0, Math.floor((Date.now() - new Date(task.running_since!).getTime()) / 1000))
    : task.tracked_seconds

  const locked = task.payment_status === 'paid' || task.payment_status === 'in_payout'
  const done = task.work_status === 'done' || task.work_status === 'cancelled'

  return (
    <>
      <Bar title={task.client_name ?? 'Задача'} onBack={onBack} />

      <div className="m-block" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className="m-sub">№ {task.id}</span>
          {task.is_support && <span className="m-chip m-chip-support">Сопровождение</span>}
          {task.priority !== 'normal' && (
            <span className={`m-chip m-chip-prio-${task.priority}`}>
              {PRIORITY_LABEL[task.priority]}
            </span>
          )}
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}>{task.title}</div>
        {task.description && (
          <div style={{ fontSize: 14, color: 'var(--m-muted)', marginTop: 8, whiteSpace: 'pre-wrap' }}>
            {task.description}
          </div>
        )}
      </div>

      <div className="m-card" style={{ margin: '14px 16px 0', textAlign: 'center', padding: '18px 14px' }}>
        {live && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, marginBottom: 8 }}>
            <span className="m-dot" />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--m-green)' }}>
              Идёт работа
            </span>
          </div>
        )}
        <div className="m-timer-value" style={{ color: live ? 'var(--m-navy)' : 'var(--m-faint)' }}>
          {formatHMS(seconds)}
        </div>
        {task.estimate !== null && (
          <div style={{ fontSize: 13, color: 'var(--m-muted)', marginTop: 8 }}>
            оценка {Number(task.estimate)} ч
          </div>
        )}
      </div>

      <div className="m-block">
        <div className="m-block-title">Сведения</div>
        <div className="m-card">
          <Row k="Дата" v={humanDate(task.work_date)} />
          {task.subclient_name && <Row k="Объект" v={task.subclient_name} />}
          {task.project_name && <Row k="Проект" v={task.project_name} />}
          {task.source_name && <Row k="Канал" v={task.source_name} />}
          <Row k="Исполнитель" v={task.assignee_name ?? '—'} />
          <Row k="Статус работы" v={WORK_STATUS_LABEL[task.work_status]} />
          <Row k="Оплата" v={PAYMENT_STATUS_LABEL[task.payment_status]} />
          <Row k="Учтено" v={formatHM(task.tracked_seconds)} />
          <Row
            k="Сумма"
            v={task.rate_missing ? 'ставка не задана' : formatMoney(Number(task.amount))}
          />
        </div>
      </div>

      {task.entries.length > 0 && (
        <div className="m-block">
          <div className="m-block-title">Отрезки времени</div>
          <div className="m-card">
            {task.entries.slice(0, 12).map((e) => (
              <Row
                key={e.id}
                k={new Date(e.started_at).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                v={e.stopped_at ? formatHM(e.seconds) : 'идёт'}
              />
            ))}
          </div>
        </div>
      )}

      {locked && (
        <div className="m-block">
          <div className="m-card" style={{ background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b', fontSize: 14 }}>
            Задача попала в ведомость. Менять её можно только через расчётчика.
          </div>
        </div>
      )}

      <div style={{ height: 16 }} />

      {!locked && (
        <div className="m-actions">
          {live ? (
            <>
              <button
                className="m-btn m-btn-pause"
                onClick={() => {
                  void queueOp('timer.stop', { taskId: task.id, finish: false })
                  onToast('Пауза')
                }}
              >
                <IconPause color="#a15c07" />
                Пауза
              </button>
              <button
                className="m-btn m-btn-stop"
                onClick={() => {
                  void queueOp('timer.stop', { taskId: task.id, finish: true })
                  onToast('Работа завершена')
                }}
              >
                <IconStop />
                Завершить
              </button>
            </>
          ) : (
            <>
              <button className="m-btn m-btn-ghost" onClick={() => setStatusOpen(true)}>
                Статус
              </button>
              {!done && (
                <button
                  className="m-btn m-btn-primary"
                  onClick={() => {
                    void queueOp('timer.start', { taskId: task.id })
                    onToast('Таймер запущен')
                  }}
                >
                  <IconPlay />
                  {task.tracked_seconds > 0 ? 'Продолжить' : 'Старт'}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {statusOpen && (
        <Sheet title="Статус работы" onClose={() => setStatusOpen(false)}>
          {PICKABLE.map((s) => (
            <button
              key={s}
              className="m-sheet-item"
              onClick={() => {
                setStatusOpen(false)
                if (s !== task.work_status) {
                  void queueOp('task.status', { taskId: task.id, status: s })
                  onToast(`Статус: ${WORK_STATUS_LABEL[s]}`)
                }
              }}
            >
              <span>{WORK_STATUS_LABEL[s]}</span>
              {s === task.work_status && <span style={{ color: 'var(--m-green)', fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </Sheet>
      )}
    </>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="m-kv">
      <span className="m-kv-k">{k}</span>
      <span className="m-kv-v">{v}</span>
    </div>
  )
}

export function Bar({ title, onBack, right }: { title: string; onBack: () => void; right?: ReactNode }) {
  return (
    <div className="m-bar">
      <button onClick={onBack} aria-label="Назад">
        <IconBack />
      </button>
      <div className="m-bar-title">{title}</div>
      {right}
    </div>
  )
}

export function Sheet({ title, onClose, children }: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div className="m-sheet-back" onClick={onClose}>
      <div className="m-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="m-sheet-grip" />
        <div className="m-sheet-title">{title}</div>
        {children}
      </div>
    </div>
  )
}
